import type { ReactNode } from "react";

import { useDndContext, useDroppable } from "@dnd-kit/core";
import { X } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { longDay, pageProblemText } from "@/lib/tina/festival-page";
import { PUBLISH_STEP_STATE_LABEL } from "@/lib/tina/festival-page";
import { cn } from "@/lib/utils";
import { DayLineup } from "@/pages/tina/festivals/components/page/day-lineup";
import {
  CreateVenueHint,
  VenuePicker,
} from "@/pages/tina/festivals/components/venue-picker";
import type {
  FormDay,
  PageFormAction,
} from "@/pages/tina/festivals/lib/page-form";
import {
  dayDropId,
  trayDragName,
} from "@/pages/tina/festivals/lib/page-request";
import type { DayBox } from "@/types/tina-festival-page";

interface DayCardProps {
  day: FormDay;
  number: number;
  /** The page's problem keys; the ones naming this day's date show under it. */
  problems: string[];
  editable: boolean;
  dispatch: (action: PageFormAction) => void;
  /** After approval: what Tina read of this day's event and its publish step. */
  box?: DayBox;
  /** The suggestions that are about this day. */
  suggestions?: ReactNode;
}

/** The venue hint under a day covers a missing venue, so its problem key is not listed a second time. */
const VENUE_KEYS = /^(venue_missing|blank_venue):/;

/** One day: its date, its venue and its lineup, and a drop target for a tray artist. */
export function DayCard({
  day,
  number,
  problems,
  editable,
  dispatch,
  box,
  suggestions,
}: DayCardProps) {
  const { setNodeRef, isOver } = useDroppable({ id: dayDropId(day.key) });
  // While a tray chip is dragged every day shows it takes a drop, and the one under the pointer lights up.
  const { active } = useDndContext();
  const trayDrag = trayDragName(active?.id) !== null;
  const hasVenue = (day.venueId ?? "").trim() !== "";
  const mine = problems.filter(
    (p) => p.endsWith(`:${day.day}`) && !VENUE_KEYS.test(p),
  );

  return (
    <div
      ref={setNodeRef}
      className={cn(
        "relative flex flex-col gap-3 rounded-lg border p-3 transition-colors",
        trayDrag && "border-dashed border-primary/60",
        trayDrag && isOver && "border-solid border-primary bg-primary/10",
      )}
    >
      {trayDrag ? (
        <p className="pointer-events-none absolute right-12 top-4 rounded bg-background px-2 text-xs font-medium text-primary">
          {isOver
            ? `Drop to put on Day ${number}`
            : `Drop here for Day ${number}`}
        </p>
      ) : null}
      <div className="flex items-center justify-between">
        <h3 className="font-medium">Day {number}</h3>
        {editable ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={`Remove Day ${number}`}
            onClick={() => dispatch({ type: "remove_day", key: day.key })}
          >
            <X className="h-4 w-4" />
          </Button>
        ) : null}
      </div>
      {box?.mood_event_id ? (
        <div className="flex flex-col gap-1 text-sm">
          <div className="flex flex-wrap items-center gap-2">
            <span>{box.name ?? ""}</span>
            {box.saves !== null ? (
              <span className="text-xs text-muted-foreground">
                {box.saves} {box.saves === 1 ? "save" : "saves"}
              </span>
            ) : null}
            <a
              href={`/events/edit/${box.mood_event_id}`}
              target="_blank"
              rel="noreferrer"
              className="text-xs underline"
            >
              Open in Mood
            </a>
          </div>
          <div className="flex flex-wrap gap-1">
            {box.mood_state === "changed_in_mood" ? (
              <Badge variant="outline" className="font-normal">
                Changed in Mood since publish
              </Badge>
            ) : null}
            {box.mood_state === "deleted_in_mood" ? (
              <Badge variant="outline" className="font-normal">
                Deleted in Mood
              </Badge>
            ) : null}
            {box.cancelled_in_mood ? (
              <Badge variant="outline" className="font-normal">
                Cancelled in Mood
              </Badge>
            ) : null}
          </div>
        </div>
      ) : null}
      {box?.state && box.state !== "done" ? (
        <p className="text-xs text-muted-foreground">
          {PUBLISH_STEP_STATE_LABEL[box.state]}
          {box.error ? `: ${box.error}` : ""}
        </p>
      ) : null}
      {box && box.not_added.length > 0 ? (
        <p className="text-xs text-amber-700">
          Not added: {box.not_added.join(", ")}
        </p>
      ) : null}
      {editable && box ? (
        <label className="flex items-center gap-2 text-sm">
          <Checkbox
            checked={day.cancelled}
            onCheckedChange={(checked) =>
              dispatch({
                type: "set_cancelled",
                key: day.key,
                on: checked === true,
              })
            }
          />
          Cancelled
        </label>
      ) : null}
      {editable ? (
        <Input
          type="date"
          aria-label={`Date of Day ${number}`}
          value={day.day}
          onChange={(event) =>
            dispatch({
              type: "set_date",
              key: day.key,
              day: event.target.value,
            })
          }
        />
      ) : (
        <p className="text-sm">{longDay(day.day)}</p>
      )}
      {editable ? (
        <VenuePicker
          label={`Venue of Day ${number}`}
          value={
            hasVenue
              ? { id: day.venueId ?? "", name: day.venueName }
              : undefined
          }
          onChange={(venue) =>
            dispatch({
              type: "set_venue",
              key: day.key,
              venue: venue ? { id: venue.id, name: venue.name } : null,
            })
          }
        />
      ) : (
        <p className="text-sm">{day.venueName || "No venue"}</p>
      )}
      {!hasVenue ? (
        <div className="flex flex-col gap-1">
          <p className="text-sm text-amber-700">Pick a venue for this day.</p>
          {day.venueName.trim() !== "" ? (
            <p className="text-xs text-muted-foreground">
              The pages said &quot;{day.venueName}&quot;.
            </p>
          ) : null}
          {editable ? <CreateVenueHint /> : null}
        </div>
      ) : null}
      {mine.length > 0 ? (
        <ul className="list-disc pl-5 text-sm text-red-700">
          {mine.map((problem) => (
            <li key={problem}>{pageProblemText(problem)}</li>
          ))}
        </ul>
      ) : null}
      <DayLineup
        dayKey={day.key}
        lineup={day.lineup}
        editable={editable}
        onChange={(lineup) =>
          dispatch({ type: "set_lineup", key: day.key, lineup })
        }
        onAdd={() => dispatch({ type: "add_artist", key: day.key })}
      />
      {suggestions}
    </div>
  );
}
